// src/pages/OrganizationAdminPage.tsx
import React, { useEffect, useState } from "react";
import SideMenu from "../../../components/sideMenu";
import Loading from "../../../components/loading";
import AlertBox from "../../../components/alertBox";
import { organizationService } from "../../../services/organizationService";
import { uploadFile } from "../../../services/uploadService";
import { AlertConfig, Organization, User } from "../../../type";
import { useNavigate } from "react-router-dom";
import { userService } from "../../../services/userService";
import { roleService } from "../../../services/roleService";
import { FiCheckCircle, FiAlertTriangle, FiXCircle } from "react-icons/fi";
import {
  FaCircleArrowLeft,
  FaBuilding,
  FaLocationDot,
  FaCalendarDays,
  FaFloppyDisk,
  FaCircleExclamation,
  FaEye,
} from "react-icons/fa6";
import DropZoneUpload from "../../../components/uploadDropZone";

const PAGE_SIZE = 10;
const PAGE_RANGE = 2;

const OrganizationAdminPage: React.FC = () => {
  const [profile, setProfile] = useState<User | null>(null);
  const [effective, setEffective] = useState<Record<string, boolean>>({});
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // modal สำหรับ platform super admin สร้าง/แก้ org
  const [openForm, setOpenForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);

  const [orgName, setOrgName] = useState("");
  const [orgAddress, setOrgAddress] = useState("");
  const [orgLogoUrl, setOrgLogoUrl] = useState("");

  const [errors, setErrors] = useState({
    name: "",
    address: "",
  });

  const validateForm = () => {
    let valid = true;
    const newErrors = { name: "", address: "" };

    if (!orgName.trim()) {
      newErrors.name = "Organization name is required.";
      valid = false;
    } else if (orgName.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters.";
      valid = false;
    }

    if (!orgAddress.trim()) {
      newErrors.address = "Address is required.";
      valid = false;
    } else if (orgAddress.trim().length < 5) {
      newErrors.address = "Address must be at least 5 characters.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const navigate = useNavigate();

  const [isPlatformSuperAdmin, setIsPlatformSuperAdmin] = useState(false);

  const canViewOrg = effective["organization.view"] || false;
  const canEditOrg = effective["organization.update"] || false;

  const resetForm = () => {
    setEditingOrg(null);
    setOrgName("");
    setOrgAddress("");
    setOrgLogoUrl("");
    setErrors({
      name: "",
      address: "",
    });
  };

  const openCreateForm = () => {
    resetForm();
    setOpenForm(true);
  };

  const openEditForm = (org: Organization) => {
    setEditingOrg(org);
    setOrgName(org.orgName);
    setOrgAddress(org.orgAddress);
    setOrgLogoUrl(org.orgLogo);
    setOpenForm(true);
  };

  const fetchOrganizations = async (page: number = 1) => {
    setIsLoading(true);
    try {
      const res = await organizationService.getOrganizations({ page, limit: PAGE_SIZE });
      setOrganizations(res.data || []);
      setTotalPages(Math.ceil((res.total || 0) / PAGE_SIZE));
    } catch (err) {
      console.error("Failed to fetch organizations", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Pagination Helper
  const getPaginationItems = (current: number, total: number) => {
    const pages: (number | string)[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > PAGE_RANGE + 2) pages.push("...");
      for (let i = Math.max(2, current - PAGE_RANGE); i <= Math.min(total - 1, current + PAGE_RANGE); i++) {
        pages.push(i);
      }
      if (current < total - PAGE_RANGE - 1) pages.push("...");
      pages.push(total);
    }
    return pages;
  };

  const showSuccessAlert = (title: string, message: string) => {
    setAlertConfig({
      type: "success",
      title,
      message,
    });
  };

  const showErrorAlert = (title: string, message: string) => {
    setAlertConfig({
      type: "error",
      title,
      message,
    });
  };

  const showConfirmDeleteAlert = (orgName: string, onConfirm: () => void) => {
    setAlertConfig({
      type: "confirm",
      title: "Confirm deletion",
      message: `Are you sure you want to delete "${orgName}"? This action cannot be undone.`,
      onConfirm,
    });
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const [profileRes, effectiveRes] = await Promise.all([
          userService.getProfile(),
          roleService.getMyEffectivePermissions(),
        ]);

        setProfile(profileRes);
        setEffective(effectiveRes.effective || {});

        if (profileRes.isPlatformSuperAdmin) {
          setIsPlatformSuperAdmin(true);
          await fetchOrganizations(currentPage);
        }
      } catch (err) {
        console.error("[OrganizationAdminPage] init error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (isPlatformSuperAdmin) {
      fetchOrganizations(currentPage);
    }
  }, [currentPage, isPlatformSuperAdmin]);

  const handleSubmitOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (editingOrg) {
        await organizationService.updateOrganization(editingOrg.id, {
          org_name: orgName,
          org_address: orgAddress,
          org_logo: orgLogoUrl,
        });
        await fetchOrganizations(currentPage);
        setOpenForm(false);
        showSuccessAlert(
          "Updated successfully",
          `Organization "${orgName}" has been updated.`
        );
        resetForm();
      } else {
        await organizationService.createOrganization({
          org_name: orgName,
          org_address: orgAddress,
          org_logo: orgLogoUrl,
        });
        setCurrentPage(1);
        await fetchOrganizations(1);
        setOpenForm(false);
        showSuccessAlert(
          "Created successfully",
          `Organization "${orgName}" has been created.`
        );
        resetForm();
      }
    } catch (err) {
      console.error("Create/Update organization failed", err);
      showErrorAlert(
        "Failed to save organization",
        "An error occurred while creating or updating the organization. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteOrgConfirmed = async (org: Organization) => {
    setIsLoading(true);
    try {
      await organizationService.deleteOrganization(org.id);
      await fetchOrganizations(currentPage);
      showSuccessAlert(
        "Deleted successfully",
        `Organization "${org.orgName}" has been deleted.`
      );
    } catch (err) {
      console.error("Delete organization failed", err);
      showErrorAlert(
        "Failed to delete organization",
        "An error occurred while deleting the organization. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteOrg = (org: Organization) => {
    showConfirmDeleteAlert(org.orgName, () => handleDeleteOrgConfirmed(org));
  };

  const myOrg = profile?.organization || null;

  const handleUpdateMyOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!myOrg) return;
    if (!canEditOrg) return;

    setIsLoading(true);
    try {
      await organizationService.updateOrganization(myOrg.id, {
        org_name: myOrg.orgName,
        org_address: myOrg.orgAddress,
        org_logo: myOrg.orgLogo,
      });
      alert("Organization profile updated.");
    } catch (err) {
      console.error("Update my organization failed", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SideMenu active={3}>
      <div className="relative">
        {isLoading && <Loading />}

        <div className="container p-4 pt-8 xl:pt-4">
            {isPlatformSuperAdmin ? (
              <>
                <div className="flex md:flex-row flex-col gap-4 justify-between mb-4">
                  <div className="flex gap-4 items-center">
                    <FaCircleArrowLeft
                      className="text-2xl cursor-pointer"
                      onClick={() => navigate(-1)}
                    />
                    <h3 className="text-xl font-bold">
                      Organization Management (Platform)
                    </h3>
                  </div>
                  <button
                    className="px-4 py-2 bg-navy text-white rounded-lg text-sm"
                    onClick={openCreateForm}
                  >
                    + New Organization
                  </button>
                </div>

                <div className="border rounded-md w-[390px] md:w-full overflow-x-auto no-scrollbar text-sm bg-white">
                  <div className="w-[900px] md:w-full">
                    <div className="grid grid-cols-6 bg-navy text-white p-3 font-semibold">
                      <div className="col-span-2">Organization</div>
                      <div className="col-span-2">Address</div>
                      <div>Created At</div>
                      <div className="text-center">Actions</div>
                    </div>

                    <div className="max-h-[500px] overflow-y-auto">
                      {organizations.map((org, idx) => (
                        <div
                          key={org.id}
                          className={`grid grid-cols-6 items-center p-3 border-b border-gray-100 ${
                            idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }`}
                        >
                          <div className="col-span-2 flex items-center gap-3">
                            <img
                              src={org.orgLogo}
                              alt="logo"
                              className="w-10 h-10 rounded-full object-cover border"
                            />
                            <span className="font-medium">{org.orgName}</span>
                          </div>
                          <div className="truncatec col-span-2">
                            {org.orgAddress}
                          </div>
                          <div>
                            {org.createdAt
                              ? new Date(org.createdAt).toLocaleDateString()
                              : "-"}
                          </div>
                          <div className="flex justify-center gap-2 ">
                            <button
                              className="px-2 py-1 text-xs rounded bg-gray-200"
                              onClick={() => openEditForm(org)}
                            >
                              Edit
                            </button>
                            <button
                              className="px-2 py-1 text-xs rounded bg-red text-white"
                              onClick={() => handleDeleteOrg(org)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                      {organizations.length === 0 && (
                        <div className="p-10 text-center text-gray-500">
                          No organizations found.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-row items-center justify-center gap-3 w-full mt-6">
                    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                      <img src="./right-arrow.svg" alt="Prev" className="w-5 rotate-180 opacity-80" />
                    </button>
                    <div className="flex space-x-2">
                      {getPaginationItems(currentPage, totalPages).map((page, i) => (
                        <button
                          key={i}
                          className={`px-3 py-1 rounded text-sm ${currentPage === page ? "bg-green text-white" : "bg-gray-200 text-black"} ${page === "..." ? "cursor-default" : ""}`}
                          onClick={() => typeof page === "number" && setCurrentPage(page)}
                          disabled={page === "..."}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
                      <img src="./right-arrow.svg" alt="Next" className="w-5 opacity-80" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex gap-4 items-center mb-6">
                  <FaCircleArrowLeft
                    className="text-2xl cursor-pointer"
                    onClick={() => navigate(-1)}
                  />
                  <h3 className="text-xl font-bold">
                    Organization Management (Platform)
                  </h3>
                </div>
                {!canViewOrg ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <FaCircleExclamation className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-500">
                      You do not have permission to view organization details.
                    </p>
                  </div>
                ) : !myOrg ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
                    <FaCircleExclamation className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-500">
                      No organization is associated with your account.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white border rounded-2xl shadow-sm">
                    {/* Organization Header Card */}
                    <div className="p-6 border-b">
                      <div className="flex xl:flex-row flex-col items-center lg:items-start gap-6">
                        <img
                          src={myOrg.orgLogo}
                          alt="logo"
                          className="w-36 h-36 lg:w-20 lg:h-20 rounded-xl object-cover border-2 shadow-sm"
                        />
                        <div className="w-full">
                          <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            {myOrg.orgName}
                          </h2>
                          <div className="flex flex-wrap gap-3">
                            <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 py-1.5 rounded-lg">
                              <FaBuilding className="w-3.5 h-3.5" />
                              <span className="text-ellipsis overflow-hidden">Organization ID: {myOrg.id}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 py-1.5 rounded-lg">
                              <FaCalendarDays className="w-3.5 h-3.5" />
                              <span>
                                Created:{" "}
                                {myOrg.createdAt
                                  ? new Date(
                                      myOrg.createdAt
                                    ).toLocaleDateString()
                                  : "-"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Form Section */}
                    <div className="p-6 space-y-5">
                      {/* Organization Name */}
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                          <FaBuilding className="w-4 h-4" />
                          Organization Name
                        </label>
                        <input
                          type="text"
                          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                          value={myOrg.orgName}
                          disabled={!canEditOrg}
                          onChange={(e) => {
                            if (!canEditOrg) return;
                            setOrgName(e.target.value);
                          }}
                        />
                      </div>

                      {/* Address */}
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                          <FaLocationDot className="w-4 h-4" />
                          Address
                        </label>
                        <textarea
                          rows={3}
                          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 resize-none"
                          value={myOrg.orgAddress}
                          disabled={!canEditOrg}
                          onChange={(e) => {
                            if (!canEditOrg) return;
                            setOrgAddress(e.target.value);
                          }}
                        />
                      </div>

                      {/* Action Footer */}
                      {canEditOrg ? (
                        <div className="flex justify-end pt-4 border-t">
                          <button
                            onClick={handleUpdateMyOrg}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-navy text-white text-sm font-medium hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: "#1e293b" }}
                          >
                            <FaFloppyDisk className="w-4 h-4" />
                            Save Changes
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 pt-4 border-t">
                          <FaEye className="w-4 h-4 text-gray-400" />
                          <p className="text-xs text-gray-500">
                            You have read-only access to your organization
                            profile.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Modal form สำหรับ platform super admin */}
          <AlertBox open={openForm}>
            <div className="w-[320px] md:w-[480px]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {editingOrg ? "Edit Organization" : "Create Organization"}
                </h3>
                <button onClick={() => setOpenForm(false)}>✕</button>
              </div>
              <form className="flex flex-col gap-4" onSubmit={handleSubmitOrg}>
                <div>
                  <label className="block text-sm mb-1">
                    Organization Name
                  </label>
                  <input
                    className={`w-full border rounded px-3 py-2 text-sm ${
                      errors.name ? "border-red" : "border-gray-300"
                    }`}
                    value={orgName}
                    onChange={(e) => {
                      setOrgName(e.target.value);
                      if (errors.name) validateForm();
                    }}
                  />
                  {errors.name && (
                    <p className="text-xs text-red mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm mb-1">Address</label>
                  <textarea
                    className={`w-full border rounded px-3 py-2 text-sm ${
                      errors.address ? "border-red" : "border-gray-300"
                    }`}
                    value={orgAddress}
                    onChange={(e) => {
                      setOrgAddress(e.target.value);
                      if (errors.address) validateForm();
                    }}
                    rows={2}
                  />
                  {errors.address && (
                    <p className="text-xs text-red">{errors.address}</p>
                  )}
                </div>
                <DropZoneUpload
                  label="Organization Logo"
                  value={orgLogoUrl}
                  onChange={setOrgLogoUrl}
                  height={150}
                  circle={false}
                />
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm rounded border"
                    onClick={() => {
                      setOpenForm(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!!errors.name || !!errors.address}
                    className={`px-4 py-2 text-sm rounded text-white
                    ${
                      !!errors.name || !!errors.address
                        ? "bg-gray-light cursor-not-allowed"
                        : "bg-navy"
                    }
                  `}
                  >
                    {editingOrg ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </AlertBox>
          <AlertBox open={!!alertConfig}>
            {alertConfig && (
              <div className="w-[300px] md:w-[360px]">
                <div className="flex items-center gap-3 mb-3">
                  <div>
                    {alertConfig.type === "success" && (
                      <FiCheckCircle className="text-green text-2xl" />
                    )}
                    {alertConfig.type === "error" && (
                      <FiXCircle className="text-red text-2xl" />
                    )}
                    {alertConfig.type === "confirm" && (
                      <FiAlertTriangle className="text-yellow text-2xl" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold">{alertConfig.title}</h3>
                </div>

                <p className="text-sm text-gray-700 mb-4 whitespace-pre-line">
                  {alertConfig.message}
                </p>

                <div className="flex justify-end gap-2">
                  {alertConfig.type === "confirm" ? (
                    <>
                      <button
                        className="px-4 py-2 text-sm rounded border"
                        onClick={() => setAlertConfig(null)}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-2 text-sm rounded bg-red text-white"
                        onClick={() => {
                          alertConfig.onConfirm?.();
                          setAlertConfig(null);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <button
                      className="px-4 py-2 text-sm rounded bg-navy text-white"
                      onClick={() => setAlertConfig(null)}
                    >
                      OK
                    </button>
                  )}
                </div>
              </div>
            )}
          </AlertBox>
      </div>
    </SideMenu>
  );
};

export default OrganizationAdminPage;